// src/services/unifiedStockService.js
// Unified Stock Management Service
// Consolidates stock operations across PackersToolkit, ProductionToolkit, and VenuesToolkit
// Provides common stock operations while maintaining compatibility with specialized services

import { db } from './firebase';
import {
    collection,
    doc,
    query,
    where,
    limit,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    Timestamp,
    writeBatch,
    addDoc,
    deleteField
} from 'firebase/firestore';
import stockMovementService, { MOVEMENT_TYPE, SOURCE_SYSTEM } from './stockMovementService.js';
import stockService from './stockService.js';
import unifiedProductService from './unifiedProductService.js';

// ============================================================================
// CONSTANTS
// ============================================================================

export const STOCK_COLLECTIONS = {
    STOCK_LEVELS: 'stockLevels',
    STOCK_LEVELS_DOC: 'stock levels',
    LOTS_SUBCOLLECTION: 'lots',
    VENUE_STOCK: 'venueStock',
    STOCK_MOVEMENTS: 'stockMovements',
    STOCK_RESERVATIONS: 'stockReservations'
};

export const STOCK_STRUCTURE_TYPE = {
    SIMPLE: 'simple',           // Just a number
    COMPLEX: 'complex',         // Object with {total, batches, lastUpdated}
    LOT_TRACKED: 'lot_tracked'  // Subcollection with lot details
};

export const SYNC_TARGET = {
    WAREHOUSE_ONLY: 'warehouse',           // Only stockLevels
    ECOMMERCE_ONLY: 'ecommerce',          // Only products_{tier}
    BOTH: 'both',                          // Both warehouse and e-commerce
    ALL: 'all'                             // Warehouse, e-commerce, and sync targets
};

// Cache configuration
const CACHE_CONFIG = {
    TTL: 5 * 60 * 1000, // 5 minutes
    ENABLED: true
};

// ============================================================================
// CACHE MANAGER
// ============================================================================

class StockCacheManager {
    constructor() {
        this.cache = new Map();
        this.timestamps = new Map();
        this.ttl = CACHE_CONFIG.TTL;
    }

    set(key, value) {
        this.cache.set(key, value);
        this.timestamps.set(key, Date.now());
        console.log(`[StockCache] Cached: ${key}`);
    }

    get(key) {
        if (!this.cache.has(key)) return null;
        
        const timestamp = this.timestamps.get(key);
        if (Date.now() - timestamp > this.ttl) {
            this.delete(key);
            return null;
        }
        
        return this.cache.get(key);
    }

    delete(key) {
        this.cache.delete(key);
        this.timestamps.delete(key);
        console.log(`[StockCache] Cleared: ${key}`);
    }

    clear() {
        this.cache.clear();
        this.timestamps.clear();
        console.log(`[StockCache] Cleared all cache`);
    }

    /**
     * Remove cache entries whose keys satisfy the predicate (e.g. all lot-stock variants).
     * @param {(key: string) => boolean} predicate
     */
    deleteKeysWhere(predicate) {
        if (typeof predicate !== 'function') return;
        for (const key of Array.from(this.cache.keys())) {
            if (typeof key === 'string' && predicate(key)) {
                this.delete(key);
            }
        }
    }

    has(key) {
        if (!this.cache.has(key)) return false;
        const timestamp = this.timestamps.get(key);
        return Date.now() - timestamp <= this.ttl;
    }

    getStats() {
        return {
            entries: this.cache.size,
            ttl: this.ttl,
            oldestEntry: this.timestamps.size > 0 ? 
                Math.min(...Array.from(this.timestamps.values())) : null
        };
    }
}

// ============================================================================
// UNIFIED STOCK SERVICE
// ============================================================================

class UnifiedStockService {
    constructor() {
        this.cache = new StockCacheManager();
        this.subscriptions = new Map();
        this.reservations = new Map(); // In-memory reservation tracking
    }

    /**
     * Lot stock is cached per SKU scope and per {includeFrozen, split}. Invalidate every variant
     * after lot writes, freeze/unfreeze, stock take sync, or main doc snapshot updates (lotStock field).
     */
    invalidateLotStockCaches() {
        this.cache.deleteKeysWhere((key) => typeof key === 'string' && key.startsWith('lot_stock'));
    }

    /**
     * Stable cache key for getLotStock (must include filter flags so frozen vs usable cannot cross-contaminate).
     */
    buildLotStockCacheKey(sku, includeFrozen, split) {
        const skuKey = sku ? String(sku).trim().toUpperCase() : 'ALL';
        return `lot_stock_cache|${skuKey}|f${includeFrozen ? 1 : 0}|s${split ? 1 : 0}`;
    }

    // ========================================================================
    // AUTHORITATIVE OVERRIDES (PackersToolkit source-of-truth)
    // ========================================================================

    async purgeReservationsForSku(sku, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        if (!normalizedSku) throw new Error('SKU is required');

        const { batchSize = 450 } = options;

        let deleted = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const q = query(
                collection(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS),
                where('sku', '==', normalizedSku),
                limit(Math.min(Math.max(Number(batchSize) || 450, 1), 450))
            );
            const snap = await getDocs(q);
            if (snap.empty) break;

            const batch = writeBatch(db);
            snap.docs.forEach((d) => batch.delete(d.ref));
            await batch.commit();
            deleted += snap.size;
        }

        return deleted;
    }

    async purgeLotsForSku(sku, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        if (!normalizedSku) throw new Error('SKU is required');

        const { batchSize = 450 } = options;

        // 1) Delete all lot docs for this SKU (subcollection)
        const lotsColRef = collection(
            db,
            STOCK_COLLECTIONS.STOCK_LEVELS,
            STOCK_COLLECTIONS.STOCK_LEVELS_DOC,
            STOCK_COLLECTIONS.LOTS_SUBCOLLECTION
        );

        let deleted = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const q = query(
                lotsColRef,
                where('sku', '==', normalizedSku),
                limit(Math.min(Math.max(Number(batchSize) || 450, 1), 450))
            );
            const snap = await getDocs(q);
            if (snap.empty) break;

            const batch = writeBatch(db);
            snap.docs.forEach((d) => batch.delete(d.ref));
            await batch.commit();
            deleted += snap.size;
        }

        // 2) Remove SKU entry from the lotStock snapshot field (single doc update)
        const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
        const stockSnap = await getDoc(stockRef);
        if (stockSnap.exists()) {
            const data = stockSnap.data() || {};
            const lotStockField = data.lotStock && typeof data.lotStock === 'object' ? data.lotStock : {};
            if (lotStockField && Object.prototype.hasOwnProperty.call(lotStockField, normalizedSku)) {
                const nextLotStock = { ...lotStockField };
                delete nextLotStock[normalizedSku];
                await setDoc(stockRef, { lotStock: nextLotStock, lastUpdated: Timestamp.now() }, { merge: true });
            }
        }

        this.invalidateLotStockCaches();

        return deleted;
    }

    /**
     * Authoritative "force set" for PackersToolkit.
     * This is designed to WIN over all other stock sources (lots/batches/reservations).
     */
    async forceSetStockAuthoritative(sku, newTotal, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        if (!normalizedSku) throw new Error('SKU is required');
        const target = Number(newTotal);
        if (!Number.isFinite(target) || target < 0) {
            throw new Error('New total must be a non-negative number');
        }

        const {
            userId = 'packers@system',
            reason = 'Authoritative stock override',
            purgeLots = true,
            purgeReservations = true,
            syncEcommerce = true,
            ecommerceTiers = ['default', 'A', 'B', 'C'],
        } = options;

        const result = {
            sku: normalizedSku,
            newTotal: target,
            lotsPurged: 0,
            reservationsPurged: 0,
            ecommerceUpdated: {},
        };

        if (purgeLots) {
            result.lotsPurged = await this.purgeLotsForSku(normalizedSku);
        }

        if (purgeReservations) {
            result.reservationsPurged = await this.purgeReservationsForSku(normalizedSku);
        }

        // Force-set warehouse stock
        await this.updateMainStock(normalizedSku, target, {
            userId,
            type: MOVEMENT_TYPE.RECONCILIATION,
            sourceSystem: SOURCE_SYSTEM.PACKERS,
            reason,
            additionalData: {
                authoritative: true,
                lotsPurged: result.lotsPurged,
                reservationsPurged: result.reservationsPurged,
            }
        });

        // Optional: force-set e-commerce tiers to match exactly
        if (syncEcommerce) {
            for (const tier of ecommerceTiers) {
                try {
                    const collectionName = `products_${String(tier || 'default')}`;
                    const productRef = doc(db, collectionName, normalizedSku);
                    const snap = await getDoc(productRef);
                    if (!snap.exists()) {
                        result.ecommerceUpdated[tier] = 'missing';
                        continue;
                    }
                    const data = snap.data() || {};
                    if (data.Stock === 'x') {
                        result.ecommerceUpdated[tier] = 'unlimited';
                        continue;
                    }
                    await updateDoc(productRef, { Stock: target });
                    result.ecommerceUpdated[tier] = target;
                } catch (e) {
                    result.ecommerceUpdated[tier] = `ERROR: ${e?.message || e}`;
                }
            }
        }

        // Clear caches + in-memory reservations (best effort)
        this.cache.delete('main_stock');
        this.reservations.clear();

        return result;
    }

    // ========================================================================
    // STOCK VALUE EXTRACTION
    // ========================================================================

    /**
     * Safely extract numeric stock value from any stock structure
     * Handles: number, {total}, {total, batches}, null, undefined
     * @param {any} stockData - Stock data in any format
     * @returns {number} Numeric stock value
     */
    extractStockValue(stockData) {
        if (stockData === null || stockData === undefined) return 0;
        if (typeof stockData === 'number') return stockData;
        if (typeof stockData === 'object' && typeof stockData.total === 'number') {
            return stockData.total;
        }
        return 0;
    }

    /**
     * Production Planner (MultiDayForecast): availability must come from lot/batch lines
     * (`stockLevels` doc → `lots` subcollection). The main aggregate field
     * (`stock levels.stock[sku]`) is ignored when there are no lot rows for the SKU
     * (after the same filters as `getLotStock`, e.g. includeFrozen). This prevents
     * orphan main totals without a batch/lot from inflating planner stock and shortfall math.
     *
     * Frozen vs fresh split matches planner UI: `isFrozen` flag or lot code F-prefix / F-suffix.
     *
     * @param {any} rawStockValue - Main doc value for this SKU (may still be read for diagnostics)
     * @param {Array<object>} lotArray - Lots array (from getLotStock / merged planner snapshot)
     * @param {number} reserved - Reserved units (subtracted from fresh only)
     * @param {boolean} includeFrozen - Include frozen lots in physical totals and frozenAvailable
     * @returns {object} Snapshot fields consumed by PlannerTable / category view
     */
    computePlannerStockSnapshotForSku(rawStockValue, lotArray, reserved, includeFrozen) {
        const lotArr = Array.isArray(lotArray) ? lotArray : [];
        const baseValue = this.extractStockValue(rawStockValue);
        const res = Math.max(Number(reserved || 0), 0);

        const isFrozenLot = (lot) => {
            if (lot?.isFrozen === true) return true;
            const code = (lot?.lotCode || lot?.consumerCode || '').toString().toUpperCase();
            if (!code) return false;
            return /^F/.test(code) || /F$/.test(code);
        };

        const totals = lotArr.reduce(
            (acc, lot) => {
                const units = Number(lot.units) || 0;
                if (units < 0) return acc;
                if (isFrozenLot(lot)) acc.frozen += units;
                else acc.fresh += units;
                return acc;
            },
            { fresh: 0, frozen: 0 }
        );

        const hasLotData = lotArr.length > 0;
        let physicalFresh;
        let physicalFrozen;
        if (hasLotData) {
            physicalFresh = totals.fresh;
            physicalFrozen = totals.frozen;
        } else {
            physicalFresh = 0;
            physicalFrozen = 0;
        }

        const physical = physicalFresh + (includeFrozen ? physicalFrozen : 0);
        const availableFresh = Math.max(physicalFresh - res, 0);
        const frozenAvailable = Math.max(includeFrozen ? physicalFrozen : 0, 0);

        return {
            total: availableFresh,
            available: availableFresh,
            availableFresh,
            frozenAvailable,
            physicalFresh,
            physicalFrozen,
            physical,
            reserved: res,
            stockSource: hasLotData ? 'lot' : 'no_lots',
            mainStockUnallocated: hasLotData ? 0 : baseValue
        };
    }

    /**
     * Determine stock structure type
     * @param {any} stockData - Stock data
     * @returns {string} Structure type (simple, complex, lot_tracked)
     */
    getStockStructureType(stockData) {
        if (typeof stockData === 'number') {
            return STOCK_STRUCTURE_TYPE.SIMPLE;
        }
        if (stockData && typeof stockData === 'object') {
            if (stockData.batches) {
                return STOCK_STRUCTURE_TYPE.LOT_TRACKED;
            }
            if (stockData.total !== undefined) {
                return STOCK_STRUCTURE_TYPE.COMPLEX;
            }
        }
        return STOCK_STRUCTURE_TYPE.SIMPLE;
    }

    // ========================================================================
    // E-COMMERCE STOCK (products_{tier} collections)
    // ========================================================================

    /**
     * Get product stock from e-commerce collections
     * @param {string} sku - Product SKU/code
     * @param {string} tier - Pricing tier (default, A, B, C)
     * @param {object} options - Fetch options
     * @returns {Promise<object>} Product with stock data or null
     */
    async getProductStock(sku, tier = 'default', options = {}) {
        if (!sku) throw new Error('SKU is required');
        
        const { useCache = true } = options;
        const cacheKey = `product_stock_${tier}_${sku}`;

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached product stock for ${sku}`);
                return cached;
            }
        }

        try {
            const collectionName = `products_${tier}`;
            const productRef = doc(db, collectionName, sku);
            const snap = await getDoc(productRef);

            if (!snap.exists()) {
                console.log(`[UnifiedStock] Product ${sku} not found in ${collectionName}`);
                return null;
            }

            const product = { id: snap.id, ...snap.data() };
            
            console.log(`[UnifiedStock] Fetched product stock: ${sku} = ${product.Stock}`);

            // Cache result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, product);
            }

            return product;

        } catch (error) {
            console.error(`[UnifiedStock] Error fetching product stock for ${sku}:`, error);
            throw error;
        }
    }

    /**
     * Get all products stock from a pricing tier
     * @param {string} tier - Pricing tier (default, A, B, C)
     * @param {object} options - Fetch options
     * @returns {Promise<Array>} Array of products with stock
     */
    async getAllProductStock(tier = 'default', options = {}) {
        const { useCache = true, skipUnlimited = false } = options;
        const cacheKey = `all_product_stock_${tier}`;

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached all products for ${tier}`);
                return cached;
            }
        }

        try {
            const collectionName = `products_${tier}`;
            const productsSnap = await getDocs(collection(db, collectionName));

            let products = productsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Filter unlimited if requested
            if (skipUnlimited) {
                products = products.filter(p => p.Stock !== 'x');
            }

            console.log(`[UnifiedStock] Fetched ${products.length} products from ${collectionName}`);

            // Cache result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, products);
            }

            return products;

        } catch (error) {
            console.error(`[UnifiedStock] Error fetching all products for ${tier}:`, error);
            throw error;
        }
    }

    // ========================================================================
    // PACKER/PRODUCTION STOCK (stockLevels collection)
    // ========================================================================

    /**
     * Get main stock levels from PackersToolkit/ProductionToolkit
     * @param {object} options - Fetch options
     * @returns {Promise<object>} Stock object {sku: value}
     */
    async getMainStock(options = {}) {
        const { useCache = true, normalize = true } = options;
        const cacheKey = 'main_stock';

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached main stock`);
                return cached;
            }
        }

        try {
            const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
            const snap = await getDoc(stockRef);

            if (!snap.exists()) {
                console.log(`[UnifiedStock] No main stock data found`);
                return {};
            }

            const data = snap.data();
            const stock = data.stock || {};

            console.log(`[UnifiedStock] Fetched main stock: ${Object.keys(stock).length} SKUs`);

            // Normalize if requested (convert all to {total, ...} structure)
            if (normalize) {
                const normalized = {};
                Object.keys(stock).forEach(sku => {
                    const value = stock[sku];
                    if (typeof value === 'number') {
                        normalized[sku] = { 
                            total: value,
                            lastUpdated: Timestamp.now(),
                            lastUpdatedBy: 'system'
                        };
                    } else if (value && typeof value === 'object') {
                        normalized[sku] = value;
                    } else {
                        normalized[sku] = { 
                            total: 0,
                            lastUpdated: Timestamp.now(),
                            lastUpdatedBy: 'system'
                        };
                    }
                });

                // Cache normalized stock
                if (useCache && CACHE_CONFIG.ENABLED) {
                    this.cache.set(cacheKey, normalized);
                }

                return normalized;
            }

            // Cache raw stock
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, stock);
            }

            return stock;
        } catch (error) {
            console.error('[UnifiedStock] Error fetching main stock:', error);
            throw error;
        }
    }

    /**
     * Update main stock for a SKU
     * @param {string} sku - Product SKU
     * @param {number} newTotal - New total stock
     * @param {object} metadata - Additional metadata
     * @returns {Promise<void>}
     */
    async updateMainStock(sku, newTotal, metadata = {}) {
        if (!sku) throw new Error('SKU is required');
        if (typeof newTotal !== 'number' || newTotal < 0) {
            throw new Error('New total must be a non-negative number');
        }

        try {
            const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
            
            // Get current stock to determine structure
            const snap = await getDoc(stockRef);
            const currentStock = snap.exists() ? (snap.data().stock || {}) : {};
            const currentValue = currentStock[sku];
            const beforeTotal = this.extractStockValue(currentValue);

            // Build update based on current structure
            let stockUpdate;
            if (typeof currentValue === 'number' || !currentValue) {
                // Simple structure
                stockUpdate = newTotal;
            } else {
                // Complex structure - preserve it
                stockUpdate = {
                    ...currentValue,
                    total: newTotal,
                    lastUpdated: Timestamp.now(),
                    lastUpdatedBy: metadata.userId || metadata.userEmail || 'system'
                };
                
                // Add optional metadata
                if (metadata.reason) stockUpdate.reason = metadata.reason;
                if (metadata.notes) stockUpdate.notes = metadata.notes;
            }

            await setDoc(stockRef, {
                stock: {
                    ...currentStock,
                    [sku]: stockUpdate
                }
            }, { merge: true });

            console.log(`[UnifiedStock] Updated stock for ${sku}: ${beforeTotal} → ${newTotal}`);

            // Log the movement
            await stockMovementService.logMovement({
                sku,
                lotCode: metadata.lotCode || null,
                type: metadata.type || MOVEMENT_TYPE.ADJUSTMENT,
                deltaUnits: newTotal - beforeTotal,
                beforeTotal,
                afterTotal: newTotal,
                sourceSystem: metadata.sourceSystem || SOURCE_SYSTEM.SYSTEM,
                sourceId: metadata.sourceId || null,
                userId: metadata.userId || metadata.userEmail || 'system',
                reason: metadata.reason || 'Stock update',
                metadata: metadata.additionalData || {}
            });

            // Clear cache
            this.cache.delete('main_stock');

        } catch (error) {
            console.error('[UnifiedStock] Error updating main stock:', error);
            throw error;
        }
    }

    /**
     * Adjust main stock (add/subtract)
     * @param {string} sku - Product SKU
     * @param {number} adjustment - Amount to adjust (positive or negative)
     * @param {object} metadata - Additional metadata
     * @returns {Promise<number>} New stock level
     */
    async adjustMainStock(sku, adjustment, metadata = {}) {
        const currentStock = await this.getMainStock({ normalize: false });
        const currentValue = this.extractStockValue(currentStock[sku]);
        const newTotal = Math.max(0, currentValue + adjustment);
        
        // updateMainStock will handle movement logging
        await this.updateMainStock(sku, newTotal, {
            ...metadata,
            type: metadata.type || (adjustment > 0 ? MOVEMENT_TYPE.PRODUCTION : MOVEMENT_TYPE.ADJUSTMENT)
        });
        
        return newTotal;
    }

    // ========================================================================
    // LOT-SPECIFIC STOCK
    // ========================================================================

    /**
     * Get lot-specific stock breakdown from the `lots` subcollection.
     * Cached keys include `includeFrozen` and `split` so a cached "usable only" map cannot be
     * mistaken for an "all lots" map (avoids phantom availability after freeze/unfreeze).
     * Call `invalidateLotStockCaches()` after any lot or embedded `lotStock` change if bypassing this service.
     * @param {string} sku - Product SKU (optional, returns all if not provided)
     * @param {object} options - Fetch options
     * @returns {Promise<object>} Lot stock data
     */
    async getLotStock(sku = null, options = {}) {
        const { useCache = true, sortByDate = true, includeFrozen = false, split = false } = options;
        const cacheKey = this.buildLotStockCacheKey(sku, includeFrozen, split);

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached lot stock`);
                return cached;
            }
        }

        try {
            const lotsColRef = collection(
                db, 
                STOCK_COLLECTIONS.STOCK_LEVELS, 
                STOCK_COLLECTIONS.STOCK_LEVELS_DOC, 
                STOCK_COLLECTIONS.LOTS_SUBCOLLECTION
            );

            let q = lotsColRef;
            if (sku) {
                q = query(lotsColRef, where('sku', '==', sku));
            }

            const lotsSnap = await getDocs(q);
            const lotsBysku = {};

            const isFrozenLotCode = (code = '') => {
                const trimmed = String(code || '').trim().toUpperCase();
                if (!trimmed) return false;
                // F at start or end is the marker (case-insensitive)
                return trimmed.startsWith('F') || trimmed.endsWith('F');
            };

            lotsSnap.docs.forEach(d => {
                const lotData = d.data();
                const lotSku = lotData.sku || '';
                const lotCode = lotData.consumerCode || lotData.lotCode || '';
                const units = Number(lotData.units || 0);

                if (!lotSku || !lotCode || units <= 0) return;

                const frozenFlag = typeof lotData.isFrozen === 'boolean'
                    ? lotData.isFrozen
                    : isFrozenLotCode(lotCode);

                const lotEntry = {
                    id: d.id,
                    lotCode,
                    sku: lotSku,
                    units,
                    productBatchId: lotData.productBatchId || null,
                    createdAt: lotData.lastRunAt || lotData.createdAt || null,
                    packSize: lotData.packSize || null,
                    isFrozen: frozenFlag,
                    frozenAt: lotData.frozenAt || null,
                    defrostedAt: lotData.defrostedAt || null,
                    frozenDurationHours: lotData.frozenDurationHours || null
                };

                if (!includeFrozen && lotEntry.isFrozen) {
                    return; // skip frozen by default
                }

                if (!lotsBysku[lotSku]) {
                    lotsBysku[lotSku] = [];
                }

                lotsBysku[lotSku].push(lotEntry);
            });

            // Sort by date if requested (FIFO)
            if (sortByDate) {
                Object.keys(lotsBysku).forEach(s => {
                    lotsBysku[s].sort((a, b) => {
                        const aTime = a.createdAt?.seconds || a.createdAt?.toMillis?.() || 0;
                        const bTime = b.createdAt?.seconds || b.createdAt?.toMillis?.() || 0;
                        return aTime - bTime;
                    });
                });
            }

            console.log(`[UnifiedStock] Fetched lot stock: ${Object.keys(lotsBysku).length} SKUs with lots`);

            // Cache the result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, lotsBysku);
            }

            if (split) {
                const result = {};
                Object.keys(lotsBysku).forEach(s => {
                    result[s] = {
                        usable: (lotsBysku[s] || []).filter(l => !l.isFrozen),
                        frozen: (lotsBysku[s] || []).filter(l => l.isFrozen)
                    };
                });
                return sku ? (result[sku] || { usable: [], frozen: [] }) : result;
            }

            return sku ? (lotsBysku[sku] || []) : lotsBysku;

        } catch (error) {
            console.error('[UnifiedStock] Error fetching lot stock:', error);
            throw error;
        }
    }

    /**
     * Upsert (or delete) a lot entry across both the lotStock snapshot field and the lots subcollection
     * @param {string} sku
     * @param {string} lotCode
     * @param {number} units
     * @param {object} metadata
     */
    async upsertLotStockEntry(sku, lotCode, units, metadata = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        const normalizedLot = String(lotCode || '').trim().toUpperCase();

        if (!normalizedSku) throw new Error('SKU is required for lot updates');
        if (!normalizedLot) throw new Error('lotCode is required for lot updates');

        const numericUnits = Number(units ?? 0);
        if (Number.isNaN(numericUnits)) {
            throw new Error('Units must be a numeric value');
        }

        const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
        const stockSnap = await getDoc(stockRef);
        const currentData = stockSnap.exists() ? stockSnap.data() : {};
        const lotStockField = currentData.lotStock || {};
        const existingLots = Array.isArray(lotStockField[normalizedSku]) ? lotStockField[normalizedSku] : [];

        const filteredLots = existingLots.filter(lot => {
            const code = (lot?.lotCode || lot?.consumerCode || '').toUpperCase();
            return code !== normalizedLot;
        });

        const isFrozenLotCode = (code = '') => {
            const trimmed = String(code || '').trim().toUpperCase();
            if (!trimmed) return false;
            return trimmed.startsWith('F') || trimmed.endsWith('F');
        };

        const isFrozen = typeof metadata.isFrozen === 'boolean'
            ? metadata.isFrozen
            : isFrozenLotCode(normalizedLot);

        if (numericUnits > 0) {
            filteredLots.push({
                lotCode: normalizedLot,
                consumerCode: normalizedLot,
                units: numericUnits,
                productBatchId: metadata.productBatchId || null,
                packSize: metadata.packSize || null,
                source: metadata.source || null,
                stockTakeId: metadata.stockTakeId || null,
                notes: metadata.notes || null,
                updatedAt: Timestamp.now(),
                createdAt: metadata.createdAt || Timestamp.now(),
                isFrozen,
                frozenAt: isFrozen ? (metadata.frozenAt || Timestamp.now()) : (metadata.frozenAt || null),
                defrostedAt: metadata.defrostedAt || null,
                frozenDurationHours: metadata.frozenDurationHours || null
            });
        }

        const updatedLotStock = { ...lotStockField };
        if (filteredLots.length > 0) {
            updatedLotStock[normalizedSku] = filteredLots;
        } else {
            delete updatedLotStock[normalizedSku];
        }

        // If units is 0, we should update main stock to reflect the deletion
        // But only if updateMainStock is not explicitly set to false (for transfers)
        const shouldUpdateMainStock = numericUnits === 0 && metadata.updateMainStock !== false;
        let updatedStock = currentData.stock || {};
        
        if (shouldUpdateMainStock) {
            const currentMainStock = this.extractStockValue(updatedStock[normalizedSku]);
            // Find the lot being removed to get its units
            const lotToRemove = existingLots.find(lot => {
                const code = (lot?.lotCode || lot?.consumerCode || '').toUpperCase();
                return code === normalizedLot;
            });
            const removedUnits = lotToRemove ? (Number(lotToRemove.units) || 0) : 0;
            
            if (removedUnits > 0) {
                const newMainStock = Math.max(0, currentMainStock - removedUnits);
                console.log(`[UnifiedStock] upsertLotStockEntry with 0 units: updating main stock for ${normalizedSku}: ${currentMainStock} → ${newMainStock} (removed ${removedUnits} units)`);
                
                updatedStock = { ...updatedStock };
                if (newMainStock > 0) {
                    updatedStock[normalizedSku] = newMainStock;
                } else {
                    delete updatedStock[normalizedSku];
                }
            }
        }

        await setDoc(stockRef, {
            stock: updatedStock,
            lotStock: updatedLotStock,
            lastUpdated: Timestamp.now(),
            lastUpdatedBy: metadata.userId || metadata.userEmail || metadata.updatedBy || 'system'
        }, { merge: true });

        const lotDocRef = doc(
            db,
            STOCK_COLLECTIONS.STOCK_LEVELS,
            STOCK_COLLECTIONS.STOCK_LEVELS_DOC,
            STOCK_COLLECTIONS.LOTS_SUBCOLLECTION,
            `${normalizedSku}__${normalizedLot}`
        );

        if (numericUnits > 0) {
            await setDoc(lotDocRef, {
                sku: normalizedSku,
                lotCode: normalizedLot,
                consumerCode: normalizedLot,
                units: numericUnits,
                productBatchId: metadata.productBatchId || null,
                packSize: metadata.packSize || null,
                source: metadata.source || null,
                notes: metadata.notes || null,
                lastRunAt: Timestamp.now(),
                updatedBy: metadata.userId || metadata.userEmail || 'system',
                isFrozen,
                frozenAt: isFrozen ? (metadata.frozenAt || Timestamp.now()) : (metadata.frozenAt || null),
                defrostedAt: metadata.defrostedAt || null,
                frozenDurationHours: metadata.frozenDurationHours || null
            }, { merge: true });
        } else {
            await deleteDoc(lotDocRef).catch(() => {
                // Doc may already be missing; ignore deletion errors
            });
        }

        this.invalidateLotStockCaches();
        if (shouldUpdateMainStock) {
            this.cache.delete('main_stock');
        }
    }

    /**
     * Delete a lot entry completely (snapshot + subcollection + optional main stock update)
     * This is a comprehensive cleanup that removes all references to the lot
     * @param {string} sku
     * @param {string} lotCode
     * @param {object} metadata - Optional metadata including userId, reason, updateMainStock, etc.
     * @param {boolean} metadata.updateMainStock - Whether to update main stock (default: true for actual deletions, false for transfers)
     */
    async deleteLotStockEntry(sku, lotCode, metadata = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        const normalizedLot = String(lotCode || '').trim().toUpperCase();

        if (!normalizedSku) throw new Error('SKU is required for lot deletion');
        if (!normalizedLot) throw new Error('lotCode is required for lot deletion');

        console.log(`[UnifiedStock] Deleting lot ${normalizedLot} for ${normalizedSku}`);

        const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
        const stockSnap = await getDoc(stockRef);
        const currentData = stockSnap.exists() ? stockSnap.data() : {};
        const lotStockField = currentData.lotStock || {};
        const existingLots = Array.isArray(lotStockField[normalizedSku]) ? lotStockField[normalizedSku] : [];

        // Find the lot being deleted to get its units for main stock adjustment
        const lotToDelete = existingLots.find(lot => {
            const code = (lot?.lotCode || lot?.consumerCode || '').toUpperCase();
            return code === normalizedLot;
        });
        const deletedUnits = lotToDelete ? (Number(lotToDelete.units) || 0) : 0;

        // Filter out the deleted lot from the array
        const filteredLots = existingLots.filter(lot => {
            const code = (lot?.lotCode || lot?.consumerCode || '').toUpperCase();
            return code !== normalizedLot;
        });

        // Calculate new lot stock total
        const newLotStockTotal = filteredLots.reduce((sum, lot) => sum + (Number(lot.units) || 0), 0);

        // Update lotStock field
        const updatedLotStock = { ...lotStockField };
        if (filteredLots.length > 0) {
            updatedLotStock[normalizedSku] = filteredLots;
        } else {
            delete updatedLotStock[normalizedSku];
        }

        // Determine if we should update main stock (default: true for actual deletions, false for transfers)
        const shouldUpdateMainStock = metadata.updateMainStock !== false; // Default to true unless explicitly set to false

        // Get current main stock to calculate new total (only if we're updating main stock)
        const currentStock = currentData.stock || {};
        const currentMainStock = this.extractStockValue(currentStock[normalizedSku]);
        let updatedStock = currentStock;
        
        if (shouldUpdateMainStock && deletedUnits > 0) {
            const newMainStock = Math.max(0, currentMainStock - deletedUnits);
            console.log(`[UnifiedStock] Lot deletion impact: ${deletedUnits} units removed, main stock: ${currentMainStock} → ${newMainStock}`);
            
            updatedStock = { ...currentStock };
            if (newMainStock > 0) {
                updatedStock[normalizedSku] = newMainStock;
            } else {
                // Remove SKU from stock if it's now zero
                delete updatedStock[normalizedSku];
            }
        } else if (!shouldUpdateMainStock) {
            console.log(`[UnifiedStock] Lot deletion (transfer mode): ${deletedUnits} units removed, main stock unchanged (${currentMainStock})`);
        }

        // Write updates together
        // IMPORTANT: Use updateDoc with explicit field paths to ensure arrays are properly replaced
        // setDoc with merge:true can sometimes not fully replace nested arrays in Firestore
        const updateData = {
            lastUpdated: Timestamp.now(),
            lastUpdatedBy: metadata.userId || metadata.userEmail || metadata.updatedBy || 'system'
        };
        
        // Update stock field - set each SKU explicitly
        Object.keys(updatedStock).forEach(skuKey => {
            updateData[`stock.${skuKey}`] = updatedStock[skuKey];
        });
        // Remove any SKUs from stock that are now zero/empty
        const previousStock = currentData.stock || {};
        Object.keys(previousStock).forEach(skuKey => {
            if (!(skuKey in updatedStock)) {
                updateData[`stock.${skuKey}`] = deleteField();
            }
        });
        
        // Update lotStock field - set each SKU array explicitly to ensure proper replacement
        Object.keys(updatedLotStock).forEach(skuKey => {
            updateData[`lotStock.${skuKey}`] = updatedLotStock[skuKey];
        });
        // Remove any SKUs from lotStock that are now empty (all lots deleted)
        const previousLotStock = currentData.lotStock || {};
        Object.keys(previousLotStock).forEach(skuKey => {
            if (!(skuKey in updatedLotStock)) {
                updateData[`lotStock.${skuKey}`] = deleteField();
            }
        });
        
        console.log(`[UnifiedStock] Updating Firestore with:`, {
            stockUpdates: Object.keys(updatedStock).length,
            lotStockUpdates: Object.keys(updatedLotStock).length,
            lotStockForSku: updatedLotStock[normalizedSku] ? updatedLotStock[normalizedSku].length : 0,
            deletedLot: normalizedLot
        });
        
        await updateDoc(stockRef, updateData);

        // Delete the lot document from subcollection
        const lotDocRef = doc(
            db,
            STOCK_COLLECTIONS.STOCK_LEVELS,
            STOCK_COLLECTIONS.STOCK_LEVELS_DOC,
            STOCK_COLLECTIONS.LOTS_SUBCOLLECTION,
            `${normalizedSku}__${normalizedLot}`
        );

        await deleteDoc(lotDocRef).catch((err) => {
            console.warn(`[UnifiedStock] Failed to delete lot subcollection doc:`, err);
        });

        // Log the movement for audit trail (only if main stock was updated)
        if (deletedUnits > 0 && shouldUpdateMainStock) {
            const newMainStock = Math.max(0, currentMainStock - deletedUnits);
            try {
                await stockMovementService.logMovement({
                    sku: normalizedSku,
                    lotCode: normalizedLot,
                    type: MOVEMENT_TYPE.ADJUSTMENT,
                    deltaUnits: -deletedUnits,
                    beforeTotal: currentMainStock,
                    afterTotal: newMainStock,
                    sourceSystem: metadata.sourceSystem || SOURCE_SYSTEM.PACKERS,
                    sourceId: metadata.sourceId || null,
                    userId: metadata.userId || metadata.userEmail || 'system',
                    reason: metadata.reason || metadata.notes || `Lot ${normalizedLot} deleted`,
                    metadata: {
                        lotDeleted: true,
                        lotCode: normalizedLot,
                        updateMainStock: true,
                        ...metadata.additionalData
                    }
                });
            } catch (movementError) {
                console.warn(`[UnifiedStock] Failed to log movement for lot deletion:`, movementError);
            }
        }

        // Clear all relevant caches
        this.invalidateLotStockCaches();
        this.cache.delete('main_stock');

        const finalMainStock = shouldUpdateMainStock ? Math.max(0, currentMainStock - deletedUnits) : currentMainStock;
        console.log(`[UnifiedStock] ✅ Lot ${normalizedLot} deleted for ${normalizedSku}. Main stock ${shouldUpdateMainStock ? `updated: ${currentMainStock} → ${finalMainStock}` : `unchanged: ${currentMainStock} (transfer mode)`}`);

        return {
            sku: normalizedSku,
            lotCode: normalizedLot,
            deletedUnits,
            previousMainStock: currentMainStock,
            newMainStock: finalMainStock,
            mainStockUpdated: shouldUpdateMainStock,
            lotsRemaining: filteredLots.length
        };
    }

    /**
     * Calculate usable stock for a SKU: main doc total vs sum of **non-frozen** lot lines.
     * When lot lines exist, `total` follows usable lots (frozen excluded) so freeze does not double-count as sellable.
     * @param {string} sku - Product SKU
     * @returns {Promise<object>} {main: number, lots: number, total: number}
     */
    async getStockTotals(sku) {
        const mainStock = await this.getMainStock({ normalize: false, useCache: false });
        const mainValue = this.extractStockValue(mainStock[sku]);

        const lots = await this.getLotStock(sku, { includeFrozen: false, useCache: false });
        const lotsTotal = Array.isArray(lots) ? 
            lots.reduce((sum, lot) => sum + lot.units, 0) : 0;

        return {
            main: mainValue,
            lots: lotsTotal,
            total: lotsTotal > 0 ? lotsTotal : mainValue, // Lots take precedence
            hasLots: lotsTotal > 0
        };
    }

    /**
     * Defrost a frozen lot (makes it usable)
     * @param {string} sku
     * @param {string} lotCode
     * @param {object} options
     */
    async defrostLot(sku, lotCode, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        const normalizedLot = String(lotCode || '').trim().toUpperCase();
        if (!normalizedSku || !normalizedLot) throw new Error('SKU and lotCode are required to defrost');

        // Load current lot to preserve units if not explicitly provided
        let currentUnits = options.units;
        if (currentUnits === undefined || currentUnits === null) {
            const lots = await this.getLotStock(normalizedSku, { includeFrozen: true, useCache: false });
            const match = Array.isArray(lots) ? lots.find(l => (l.lotCode || '').toUpperCase() === normalizedLot) : null;
            currentUnits = match?.units ?? 0;
        }

        const frozenAt = options.frozenAt || null;
        const defrostedAt = Timestamp.now();
        let durationHours = null;
        if (frozenAt?.toDate) {
            durationHours = Math.max(0, (defrostedAt.toMillis() - frozenAt.toMillis()) / (1000 * 60 * 60));
        }

        await this.upsertLotStockEntry(normalizedSku, normalizedLot, currentUnits, {
            ...options,
            isFrozen: false,
            frozenAt: frozenAt || options.frozenAt || null,
            defrostedAt,
            frozenDurationHours: options.frozenDurationHours || durationHours
        });

        // Log defrost event for traceability
        try {
            await stockMovementService.logMovement({
                sku: normalizedSku,
                lotCode: normalizedLot,
                type: MOVEMENT_TYPE.DEFROST,
                deltaUnits: currentUnits,
                beforeTotal: 0,
                afterTotal: currentUnits,
                sourceSystem: options.sourceSystem || SOURCE_SYSTEM.PACKERS,
                sourceId: options.sourceId || null,
                userId: options.userId || options.userEmail || 'system',
                reason: options.reason || `Defrosted lot ${normalizedLot}`,
                metadata: {
                    frozenAt: frozenAt || options.frozenAt || null,
                    defrostedAt,
                    frozenDurationHours: durationHours
                }
            });
        } catch (err) {
            console.warn('[UnifiedStock] Failed to log defrost movement:', err);
        }

        this.invalidateLotStockCaches();
    }

    /**
     * Freeze a lot by marking it frozen AND (optionally) renaming the lot code to include trailing 'F'.
     * This makes it automatically excluded from FIFO selection/packing and (by default) planning totals.
     * @param {string} sku
     * @param {string} lotCode
     * @param {object} options
     * @returns {Promise<{fromLot:string,toLot:string,units:number}>}
     */
    async freezeLot(sku, lotCode, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        const fromLot = String(lotCode || '').trim().toUpperCase();
        if (!normalizedSku || !fromLot) throw new Error('SKU and lotCode are required to freeze');

        const now = Timestamp.now();
        const userId = options.userId || options.userEmail || 'system';

        const lots = await this.getLotStock(normalizedSku, { includeFrozen: true, useCache: false });
        const match = Array.isArray(lots) ? lots.find(l => (l.lotCode || '').toUpperCase() === fromLot) : null;
        const currentUnits = Number(options.units ?? match?.units ?? 0);

        if (!Number.isFinite(currentUnits) || currentUnits < 0) {
            throw new Error('Invalid units for freeze operation');
        }

        // Enforce the "F at end" convention
        const toLot = fromLot.endsWith('F') ? fromLot : `${fromLot}F`;
        if (toLot === fromLot && match?.isFrozen === true) {
            return { fromLot, toLot, units: currentUnits };
        }

        const target = Array.isArray(lots) ? lots.find(l => (l.lotCode || '').toUpperCase() === toLot) : null;
        const targetUnits = Number(target?.units || 0);
        const mergedUnits = Math.max(0, targetUnits + currentUnits);

        // Preserve earliest createdAt for FIFO age
        const createdAtCandidates = [match?.createdAt, target?.createdAt].filter(Boolean);
        const createdAt = createdAtCandidates.length > 0 ? createdAtCandidates.reduce((min, ts) => {
            const millis = ts?.toMillis?.() || (ts?.seconds ? ts.seconds * 1000 : 0);
            const minMillis = min?.toMillis?.() || (min?.seconds ? min.seconds * 1000 : Number.MAX_SAFE_INTEGER);
            return millis < minMillis ? ts : min;
        }, createdAtCandidates[0]) : now;

        await this.upsertLotStockEntry(normalizedSku, toLot, mergedUnits, {
            ...options,
            isFrozen: true,
            createdAt,
            frozenAt: options.frozenAt || match?.frozenAt || now,
            defrostedAt: null,
        });

        if (toLot !== fromLot) {
            await this.deleteLotStockEntry(normalizedSku, fromLot, {
                userId,
                reason: options.reason || `Freeze lot ${fromLot} → ${toLot}`,
                updateMainStock: false, // Transfer operation - don't change main stock
                sourceSystem: options.sourceSystem || SOURCE_SYSTEM.PACKERS
            });
        }

        // Log freeze event for traceability
        try {
            await stockMovementService.logMovement({
                sku: normalizedSku,
                lotCode: toLot,
                type: MOVEMENT_TYPE.FREEZE,
                deltaUnits: currentUnits,
                beforeTotal: 0,
                afterTotal: currentUnits,
                sourceSystem: options.sourceSystem || SOURCE_SYSTEM.PACKERS,
                sourceId: options.sourceId || null,
                userId,
                reason: options.reason || `Froze lot ${fromLot} → ${toLot}`,
                metadata: {
                    fromLot,
                    toLot,
                    mergedIntoExisting: !!target,
                    frozenAt: options.frozenAt || match?.frozenAt || now,
                }
            });
        } catch (err) {
            console.warn('[UnifiedStock] Failed to log freeze movement:', err);
        }

        this.invalidateLotStockCaches();

        return { fromLot, toLot, units: currentUnits };
    }

    /**
     * Unfreeze a lot by marking it usable AND renaming the lot code to remove trailing 'F' if present.
     * @param {string} sku
     * @param {string} lotCode
     * @param {object} options
     * @returns {Promise<{fromLot:string,toLot:string,units:number}>}
     */
    async unfreezeLot(sku, lotCode, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        const fromLot = String(lotCode || '').trim().toUpperCase();
        if (!normalizedSku || !fromLot) throw new Error('SKU and lotCode are required to unfreeze');

        const now = Timestamp.now();
        const userId = options.userId || options.userEmail || 'system';

        const lots = await this.getLotStock(normalizedSku, { includeFrozen: true, useCache: false });
        const match = Array.isArray(lots) ? lots.find(l => (l.lotCode || '').toUpperCase() === fromLot) : null;
        const currentUnits = Number(options.units ?? match?.units ?? 0);
        if (!Number.isFinite(currentUnits) || currentUnits < 0) {
            throw new Error('Invalid units for unfreeze operation');
        }

        const toLot = fromLot.endsWith('F') ? fromLot.slice(0, -1) : fromLot;
        if (!toLot) throw new Error('Invalid target lot code for unfreeze');

        const target = Array.isArray(lots) ? lots.find(l => (l.lotCode || '').toUpperCase() === toLot) : null;
        const targetUnits = Number(target?.units || 0);
        const mergedUnits = Math.max(0, targetUnits + currentUnits);

        const createdAtCandidates = [match?.createdAt, target?.createdAt].filter(Boolean);
        const createdAt = createdAtCandidates.length > 0 ? createdAtCandidates.reduce((min, ts) => {
            const millis = ts?.toMillis?.() || (ts?.seconds ? ts.seconds * 1000 : 0);
            const minMillis = min?.toMillis?.() || (min?.seconds ? min.seconds * 1000 : Number.MAX_SAFE_INTEGER);
            return millis < minMillis ? ts : min;
        }, createdAtCandidates[0]) : now;

        await this.upsertLotStockEntry(normalizedSku, toLot, mergedUnits, {
            ...options,
            isFrozen: false,
            createdAt,
            frozenAt: options.frozenAt || match?.frozenAt || null,
            defrostedAt: now,
        });

        if (toLot !== fromLot) {
            await this.deleteLotStockEntry(normalizedSku, fromLot, {
                userId,
                reason: options.reason || `Unfreeze lot ${fromLot} → ${toLot}`,
                updateMainStock: false, // Transfer operation - don't change main stock
                sourceSystem: options.sourceSystem || SOURCE_SYSTEM.PACKERS
            });
        }

        try {
            await stockMovementService.logMovement({
                sku: normalizedSku,
                lotCode: toLot,
                type: MOVEMENT_TYPE.DEFROST,
                deltaUnits: currentUnits,
                beforeTotal: 0,
                afterTotal: currentUnits,
                sourceSystem: options.sourceSystem || SOURCE_SYSTEM.PACKERS,
                sourceId: options.sourceId || null,
                userId,
                reason: options.reason || `Unfroze lot ${fromLot} → ${toLot}`,
                metadata: {
                    fromLot,
                    toLot,
                    mergedIntoExisting: !!target,
                    defrostedAt: now,
                    frozenAt: options.frozenAt || match?.frozenAt || null,
                }
            });
        } catch (err) {
            console.warn('[UnifiedStock] Failed to log unfreeze movement:', err);
        }

        this.invalidateLotStockCaches();

        return { fromLot, toLot, units: currentUnits };
    }

    /**
     * Transfer units between two lot codes for the same SKU (used by Freezer Manager).
     * Does NOT change main stock totals (only lot availability).
     *
     * Typical usage:
     * - Freeze partial: fromLot="0680" -> toLot="0680F"
     * - Unfreeze partial: fromLot="0680F" -> toLot="0680"
     */
    async transferLotUnits(sku, fromLotCode, toLotCode, units, options = {}) {
        const normalizedSku = String(sku || '').trim().toUpperCase();
        const fromLot = String(fromLotCode || '').trim().toUpperCase();
        const toLot = String(toLotCode || '').trim().toUpperCase();
        const qty = Number(units || 0);

        if (!normalizedSku || !fromLot || !toLot) throw new Error('SKU, fromLotCode and toLotCode are required');
        if (!Number.isFinite(qty) || qty <= 0) throw new Error('Transfer units must be a positive number');
        if (fromLot === toLot) throw new Error('fromLotCode and toLotCode must differ');

        const now = Timestamp.now();
        const userId = options.userId || options.userEmail || 'system';

        const lots = await this.getLotStock(normalizedSku, { includeFrozen: true, useCache: false });
        const getMatch = (code) =>
            Array.isArray(lots) ? lots.find(l => (l.lotCode || l.consumerCode || '').toUpperCase() === code) : null;

        const fromMatch = getMatch(fromLot);
        const toMatch = getMatch(toLot);

        const fromUnits = Number(fromMatch?.units || 0);
        const toUnits = Number(toMatch?.units || 0);

        if (fromUnits < qty) {
            throw new Error(`Insufficient units in ${fromLot}: have ${fromUnits}, need ${qty}`);
        }

        const newFromUnits = Math.max(0, fromUnits - qty);
        const newToUnits = Math.max(0, toUnits + qty);

        const createdAtCandidates = [fromMatch?.createdAt, toMatch?.createdAt].filter(Boolean);
        const createdAt = createdAtCandidates.length > 0 ? createdAtCandidates.reduce((min, ts) => {
            const millis = ts?.toMillis?.() || (ts?.seconds ? ts.seconds * 1000 : 0);
            const minMillis = min?.toMillis?.() || (min?.seconds ? min.seconds * 1000 : Number.MAX_SAFE_INTEGER);
            return millis < minMillis ? ts : min;
        }, createdAtCandidates[0]) : now;

        const isFrozenLotCode = (code = '') => {
            const trimmed = String(code || '').trim().toUpperCase();
            if (!trimmed) return false;
            return trimmed.startsWith('F') || trimmed.endsWith('F');
        };

        const toIsFrozen = typeof options.toIsFrozen === 'boolean' ? options.toIsFrozen : isFrozenLotCode(toLot);

        await this.upsertLotStockEntry(normalizedSku, toLot, newToUnits, {
            productBatchId: options.productBatchId || toMatch?.productBatchId || fromMatch?.productBatchId || null,
            packSize: options.packSize || toMatch?.packSize || fromMatch?.packSize || null,
            source: options.source || 'freezer_transfer',
            userId,
            createdAt,
            isFrozen: toIsFrozen,
            frozenAt: toIsFrozen ? (options.frozenAt || toMatch?.frozenAt || fromMatch?.frozenAt || now) : (options.frozenAt || null),
            defrostedAt: toIsFrozen ? null : (options.defrostedAt || now),
            notes: options.notes || null
        });

        if (newFromUnits > 0) {
            await this.upsertLotStockEntry(normalizedSku, fromLot, newFromUnits, {
                productBatchId: fromMatch?.productBatchId || null,
                packSize: fromMatch?.packSize || null,
                source: options.source || 'freezer_transfer',
                userId,
                createdAt: fromMatch?.createdAt || now,
                isFrozen: fromMatch?.isFrozen === true || isFrozenLotCode(fromLot),
                frozenAt: fromMatch?.frozenAt || null,
                defrostedAt: fromMatch?.defrostedAt || null,
                notes: options.notes || null
            });
        } else {
            await this.deleteLotStockEntry(normalizedSku, fromLot, {
                userId,
                reason: options.reason || `Transferred all units out of ${fromLot}`,
                updateMainStock: false // Transfer operation - units moved to toLot, main stock unchanged
            });
        }

        try {
            const movementType = options.movementType || (toIsFrozen ? MOVEMENT_TYPE.FREEZE : MOVEMENT_TYPE.DEFROST);
            await stockMovementService.logMovement({
                sku: normalizedSku,
                lotCode: toLot,
                type: movementType,
                deltaUnits: qty,
                beforeTotal: 0,
                afterTotal: qty,
                sourceSystem: options.sourceSystem || SOURCE_SYSTEM.PACKERS,
                sourceId: options.sourceId || null,
                userId,
                reason: options.reason || `Transferred ${qty} units ${fromLot} → ${toLot}`,
                metadata: {
                    fromLot,
                    toLot,
                    qty,
                    fromBefore: fromUnits,
                    fromAfter: newFromUnits,
                    toBefore: toUnits,
                    toAfter: newToUnits
                }
            });
        } catch (err) {
            console.warn('[UnifiedStock] Failed to log freezer transfer movement:', err);
        }

        this.invalidateLotStockCaches();

        return { sku: normalizedSku, fromLot, toLot, qty, fromAfter: newFromUnits, toAfter: newToUnits };
    }

    // ========================================================================
    // REAL-TIME SUBSCRIPTIONS
    // ========================================================================

    /**
     * Subscribe to main stock changes
     * @param {function} callback - Callback function(stock)
     * @returns {function} Unsubscribe function
     */
    subscribeToMainStock(callback) {
        const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
        
        const unsubscribe = onSnapshot(stockRef, (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                const stock = data.stock || {};
                console.log(`[UnifiedStock] Stock updated: ${Object.keys(stock).length} SKUs`);
                
                // Clear cache on update (main `stock` map and embedded `lotStock` live on same doc)
                this.cache.delete('main_stock');
                this.invalidateLotStockCaches();

                callback(stock);
            } else {
                callback({});
            }
        }, (error) => {
            console.error('[UnifiedStock] Subscription error:', error);
        });

        // Store subscription
        const subId = `main_stock_${Date.now()}`;
        this.subscriptions.set(subId, unsubscribe);

        // Return enhanced unsubscribe
        return () => {
            unsubscribe();
            this.subscriptions.delete(subId);
        };
    }

    // ========================================================================
    // VENUE & MARKET STOCK
    // ========================================================================

    /**
     * Get stock for a specific venue
     * @param {string} venueId - Venue ID
     * @param {string} sku - Product SKU (optional, returns all if not provided)
     * @param {object} options - Fetch options
     * @returns {Promise<Array|object>} Venue stock items or single item
     */
    async getVenueStock(venueId, sku = null, options = {}) {
        if (!venueId) throw new Error('Venue ID is required');
        
        const { useCache = true } = options;
        const cacheKey = `venue_stock_${venueId}_${sku || 'all'}`;

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached venue stock`);
                return cached;
            }
        }

        try {
            let q = query(
                collection(db, STOCK_COLLECTIONS.VENUE_STOCK),
                where('venueId', '==', venueId)
            );

            if (sku) {
                q = query(q, where('code', '==', sku));
            }

            const snap = await getDocs(q);
            const stock = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const result = sku ? (stock[0] || null) : stock;

            console.log(`[UnifiedStock] Fetched venue stock for ${venueId}: ${stock.length} items`);

            // Cache result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, result);
            }

            return result;

        } catch (error) {
            console.error(`[UnifiedStock] Error fetching venue stock:`, error);
            throw error;
        }
    }

    /**
     * Get stock for a specific market
     * @param {string} marketId - Market ID
     * @param {string} sku - Product SKU (optional)
     * @param {object} options - Fetch options
     * @returns {Promise<Array|object>} Market stock items or single item
     */
    async getMarketStock(marketId, sku = null, options = {}) {
        if (!marketId) throw new Error('Market ID is required');
        
        const { useCache = true } = options;
        const cacheKey = `market_stock_${marketId}_${sku || 'all'}`;

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached market stock`);
                return cached;
            }
        }

        try {
            let q = query(
                collection(db, 'marketStock'),
                where('marketId', '==', marketId)
            );

            if (sku) {
                q = query(q, where('code', '==', sku));
            }

            const snap = await getDocs(q);
            const stock = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const result = sku ? (stock[0] || null) : stock;

            console.log(`[UnifiedStock] Fetched market stock for ${marketId}: ${stock.length} items`);

            // Cache result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, result);
            }

            return result;

        } catch (error) {
            console.error(`[UnifiedStock] Error fetching market stock:`, error);
            throw error;
        }
    }

    // ========================================================================
    // PRODUCTION BATCHES & INGREDIENTS
    // ========================================================================

    /**
     * Get ingredient batch
     * @param {string} batchId - Ingredient batch ID
     * @param {object} options - Fetch options
     * @returns {Promise<object>} Ingredient batch or null
     */
    async getIngredientBatch(batchId, options = {}) {
        if (!batchId) throw new Error('Batch ID is required');
        
        const { useCache = true } = options;
        const cacheKey = `ingredient_batch_${batchId}`;

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached ingredient batch ${batchId}`);
                return cached;
            }
        }

        try {
            const batchRef = doc(db, 'IngredientBatches', batchId);
            const snap = await getDoc(batchRef);

            if (!snap.exists()) {
                console.log(`[UnifiedStock] Ingredient batch ${batchId} not found`);
                return null;
            }

            const batch = { id: snap.id, ...snap.data() };

            console.log(`[UnifiedStock] Fetched ingredient batch: ${batchId} (${batch.remainingKg}kg remaining)`);

            // Cache result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, batch);
            }

            return batch;

        } catch (error) {
            console.error(`[UnifiedStock] Error fetching ingredient batch:`, error);
            throw error;
        }
    }

    /**
     * Get product batch (WIP production)
     * @param {string} batchId - Product batch ID
     * @param {object} options - Fetch options
     * @returns {Promise<object>} Product batch or null
     */
    async getProductBatch(batchId, options = {}) {
        if (!batchId) throw new Error('Batch ID is required');
        
        const { useCache = true } = options;
        const cacheKey = `product_batch_${batchId}`;

        // Check cache
        if (useCache && CACHE_CONFIG.ENABLED) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[UnifiedStock] Returning cached product batch ${batchId}`);
                return cached;
            }
        }

        try {
            const batchRef = doc(db, 'ProductBatches', batchId);
            const snap = await getDoc(batchRef);

            if (!snap.exists()) {
                console.log(`[UnifiedStock] Product batch ${batchId} not found`);
                return null;
            }

            const batch = { id: snap.id, ...snap.data() };

            console.log(`[UnifiedStock] Fetched product batch: ${batchId} (${batch.remainingKg}kg, stage: ${batch.stage})`);

            // Cache result
            if (useCache && CACHE_CONFIG.ENABLED) {
                this.cache.set(cacheKey, batch);
            }

            return batch;

        } catch (error) {
            console.error(`[UnifiedStock] Error fetching product batch:`, error);
            throw error;
        }
    }

    // ========================================================================
    // UNIFIED WRITE OPERATIONS (Phase 3 - Two-Phase Commits)
    // ========================================================================

    /**
     * Update stock with two-phase commit (sync across collections)
     * @param {string} sku - Product SKU
     * @param {number} delta - Amount to add (positive) or subtract (negative)
     * @param {object} metadata - Required metadata
     * @returns {Promise<object>} Result with before/after values
     */
    async updateStock(sku, delta, metadata = {}) {
        if (!sku) throw new Error('SKU is required');
        if (typeof delta !== 'number') throw new Error('Delta must be a number');

        const {
            type = MOVEMENT_TYPE.ADJUSTMENT,
            sourceSystem = SOURCE_SYSTEM.SYSTEM,
            sourceId = null,
            userId = 'system',
            reason = 'Stock update',
            tier = 'default',
            syncTo = SYNC_TARGET.BOTH,  // Sync to both warehouse and e-commerce
            lotCode = null,
            allowNegative = false
        } = metadata;

        try {
            console.log(`[UnifiedStock] Two-phase update for ${sku}: delta ${delta}`, {
                type,
                syncTo,
                tier
            });

            let warehouseResult = null;
            let ecommerceResult = null;
            const results = { success: false, warehouse: null, ecommerce: null, rollback: false };

            // ===== PHASE 1: Update Warehouse Stock =====
            if (syncTo === SYNC_TARGET.WAREHOUSE_ONLY || syncTo === SYNC_TARGET.BOTH || syncTo === SYNC_TARGET.ALL) {
                try {
                    const currentStock = await this.getMainStock({ normalize: false });
                    const beforeTotal = this.extractStockValue(currentStock[sku]);
                    const newTotal = allowNegative ? beforeTotal + delta : Math.max(0, beforeTotal + delta);

                    await this.updateMainStock(sku, newTotal, {
                        userId,
                        type,
                        sourceSystem,
                        sourceId,
                        reason,
                        lotCode,
                        additionalData: metadata.additionalData || {}
                    });

                    warehouseResult = { before: beforeTotal, after: newTotal, delta: newTotal - beforeTotal };
                    results.warehouse = warehouseResult;
                    console.log(`[UnifiedStock] ✅ Warehouse updated: ${beforeTotal} → ${newTotal}`);

                } catch (error) {
                    console.error(`[UnifiedStock] ❌ Warehouse update failed:`, error);
                    throw new Error(`Warehouse update failed: ${error.message}`);
                }
            }

            // ===== PHASE 2: Update E-Commerce Stock =====
            if (syncTo === SYNC_TARGET.ECOMMERCE_ONLY || syncTo === SYNC_TARGET.BOTH || syncTo === SYNC_TARGET.ALL) {
                try {
                    const collectionName = `products_${tier}`;
                    const productRef = doc(db, collectionName, sku);
                    const productSnap = await getDoc(productRef);

                    if (!productSnap.exists()) {
                        console.warn(`[UnifiedStock] Product ${sku} not found in ${collectionName}, skipping e-commerce sync`);
                    } else {
                        const productData = productSnap.data();
                        const currentStock = productData.Stock;

                        // Skip unlimited stock
                        if (currentStock === 'x') {
                            console.log(`[UnifiedStock] Skipping unlimited stock product in ${collectionName}`);
                            ecommerceResult = { before: 'unlimited', after: 'unlimited', delta: 0 };
                        } else {
                            const beforeTotal = this.extractStockValue(currentStock);
                            const newTotal = allowNegative ? beforeTotal + delta : Math.max(0, beforeTotal + delta);

                            await updateDoc(productRef, {
                                Stock: newTotal
                            });

                            ecommerceResult = { before: beforeTotal, after: newTotal, delta: newTotal - beforeTotal };
                            console.log(`[UnifiedStock] ✅ E-commerce updated (${collectionName}): ${beforeTotal} → ${newTotal}`);

                            // Log movement for e-commerce sync
                            await stockMovementService.logMovement({
                                sku,
                                lotCode,
                                type,
                                deltaUnits: newTotal - beforeTotal,
                                beforeTotal,
                                afterTotal: newTotal,
                                sourceSystem: sourceSystem + '_ecommerce_sync',
                                sourceId,
                                userId,
                                reason: reason + ' (e-commerce sync)',
                                metadata: { tier, syncFrom: 'warehouse', ...metadata.additionalData }
                            });
                        }

                        results.ecommerce = ecommerceResult;
                    }

                } catch (error) {
                    console.error(`[UnifiedStock] ❌ E-commerce sync failed:`, error);
                    
                    // ===== ROLLBACK LOGIC =====
                    if (warehouseResult && syncTo === SYNC_TARGET.BOTH) {
                        console.warn(`[UnifiedStock] 🔄 Rolling back warehouse update due to e-commerce failure`);
                        try {
                            await this.updateMainStock(sku, warehouseResult.before, {
                                userId: 'system',
                                type: MOVEMENT_TYPE.ADJUSTMENT,
                                sourceSystem: SOURCE_SYSTEM.SYSTEM,
                                reason: 'Rollback due to sync failure',
                                additionalData: { rollbackReason: error.message, originalDelta: delta }
                            });
                            results.rollback = true;
                            console.log(`[UnifiedStock] ✅ Rollback completed`);
                        } catch (rollbackError) {
                            console.error(`[UnifiedStock] ❌ CRITICAL: Rollback failed!`, rollbackError);
                            // Stock is now inconsistent - needs manual intervention
                        }
                    }
                    
                    throw new Error(`E-commerce sync failed: ${error.message}`);
                }
            }

            // Clear caches
            this.cache.delete('main_stock');
            this.cache.delete(`product_stock_${tier}_${sku}`);
            this.cache.delete(`all_product_stock_${tier}`);

            results.success = true;
            return results;

        } catch (error) {
            console.error(`[UnifiedStock] updateStock failed for ${sku}:`, error);
            throw error;
        }
    }

    /**
     * Set stock to specific value (with two-phase commit)
     * @param {string} sku - Product SKU
     * @param {number} newValue - New stock value
     * @param {object} metadata - Required metadata
     * @returns {Promise<object>} Result
     */
    async setStock(sku, newValue, metadata = {}) {
        // Calculate delta from current stock
        const currentStock = await this.getMainStock({ normalize: false });
        const currentValue = this.extractStockValue(currentStock[sku]);
        const delta = newValue - currentValue;

        return await this.updateStock(sku, delta, {
            ...metadata,
            type: metadata.type || MOVEMENT_TYPE.ADJUSTMENT
        });
    }

    /**
     * Transfer stock between locations
     * @param {string} sku - Product SKU
     * @param {string} fromLocation - Source location (warehouse, venue, market)
     * @param {string} toLocation - Destination location
     * @param {number} units - Units to transfer
     * @param {object} metadata - Metadata
     * @returns {Promise<object>} Transfer result
     */
    async transferStock(sku, fromLocation, toLocation, units, metadata = {}) {
        if (!sku) throw new Error('SKU is required');
        if (!fromLocation || !toLocation) throw new Error('From and to locations required');
        if (typeof units !== 'number' || units <= 0) throw new Error('Units must be positive number');

        const { userId = 'system', reason = 'Stock transfer' } = metadata;

        try {
            console.log(`[UnifiedStock] Transfer ${units} units of ${sku}: ${fromLocation} → ${toLocation}`);

            // Deduct from source
            await this.updateStock(sku, -units, {
                type: MOVEMENT_TYPE.VENUE_TRANSFER,
                sourceSystem: SOURCE_SYSTEM.SYSTEM,
                sourceId: `transfer_${Date.now()}`,
                userId,
                reason: `${reason} (from ${fromLocation})`,
                syncTo: fromLocation === 'warehouse' ? SYNC_TARGET.WAREHOUSE_ONLY : SYNC_TARGET.ECOMMERCE_ONLY,
                tier: metadata.tier || 'default'
            });

            // Add to destination
            await this.updateStock(sku, units, {
                type: MOVEMENT_TYPE.VENUE_TRANSFER,
                sourceSystem: SOURCE_SYSTEM.SYSTEM,
                sourceId: `transfer_${Date.now()}`,
                userId,
                reason: `${reason} (to ${toLocation})`,
                syncTo: toLocation === 'warehouse' ? SYNC_TARGET.WAREHOUSE_ONLY : SYNC_TARGET.ECOMMERCE_ONLY,
                tier: metadata.tier || 'default'
            });

            console.log(`[UnifiedStock] ✅ Transfer completed: ${fromLocation} → ${toLocation}`);

            return {
                success: true,
                sku,
                units,
                from: fromLocation,
                to: toLocation
            };

        } catch (error) {
            console.error(`[UnifiedStock] Transfer failed:`, error);
            throw error;
        }
    }

    /**
     * Transfer stock from warehouse to a venue.
     * Deducts warehouse only (no e-commerce sync), adds to venue stock.
     *
     * @param {string} sku - Product SKU
     * @param {string} venueId - Destination venue ID
     * @param {number} units - Units to transfer
     * @param {object} metadata - Metadata
     * @returns {Promise<object>} Transfer result
     */
    async transferStockToVenue(sku, venueId, units, metadata = {}) {
        if (!sku) throw new Error('SKU is required');
        if (!venueId) throw new Error('Venue ID is required');
        if (typeof units !== 'number' || units <= 0) throw new Error('Units must be a positive number');

        const { userId = 'system', reason = 'Warehouse to venue transfer' } = metadata;

        try {
            console.log(`[UnifiedStock] Transfer ${units} units of ${sku} to venue ${venueId}`);

            // 1. Deduct from warehouse only (no e-commerce)
            await this.updateStock(sku, -units, {
                type: MOVEMENT_TYPE.VENUE_TRANSFER,
                sourceSystem: SOURCE_SYSTEM.VENUE,
                sourceId: venueId,
                userId,
                reason: `${reason} (to venue ${venueId})`,
                syncTo: SYNC_TARGET.WAREHOUSE_ONLY,
                tier: metadata.tier || 'default'
            });

            // 2. Add to venue stock
            const venueStock = await stockService.getVenueStock(venueId, { skipCache: true });
            const stockItem = venueStock.find(s => String(s.code) === String(sku));
            if (stockItem) {
                await stockService.recordMovement(stockItem.id, {
                    type: 'delivery',
                    quantity: units,
                    reason: reason || `Transfer from warehouse`,
                    reference: `wh-transfer-${Date.now()}`
                });
            } else {
                const product = unifiedProductService.getProduct(sku);
                if (product) {
                    await stockService.createStockItem({
                        venueId,
                        code: sku,
                        name: product.name || sku,
                        packSize: product.packSize || '',
                        unitPrice: product.price || 0,
                        minimumLevel: 0,
                        currentStock: units,
                        category: product.category || ''
                    });
                } else {
                    throw new Error(`Product ${sku} not found - cannot add to venue`);
                }
            }

            console.log(`[UnifiedStock] ✅ Transfer to venue completed: ${sku} x${units} → ${venueId}`);
            return { success: true, sku, venueId, units };
        } catch (error) {
            console.error(`[UnifiedStock] Transfer to venue failed:`, error);
            throw error;
        }
    }

    // ========================================================================
    // STOCK RESERVATIONS (Prevent Double-Selling)
    // ========================================================================

    /**
     * Reserve stock for an order (prevents overselling during checkout)
     * @param {string} sku - Product SKU
     * @param {number} units - Units to reserve
     * @param {string} orderId - Order/cart ID
     * @param {object} metadata - Metadata
     * @returns {Promise<object>} Reservation result
     */
    async reserveStock(sku, units, orderId, metadata = {}) {
        if (!sku) throw new Error('SKU is required');
        if (!orderId) throw new Error('Order ID is required');
        if (typeof units !== 'number' || units <= 0) throw new Error('Units must be positive');

        const { 
            userId = 'system',
            tier = 'default',
            expiresIn = 15 * 60 * 1000  // 15 minutes default
        } = metadata;

        try {
            // Check available stock (not reserved)
            const currentStock = await this.getMainStock({ normalize: false });
            const total = this.extractStockValue(currentStock[sku]);
            
            // Get existing reservations for this SKU
            const reservedUnits = await this.getReservedUnits(sku);
            const available = total - reservedUnits;

            if (available < units) {
                throw new Error(`Insufficient stock: ${available} available, ${units} requested (${reservedUnits} reserved)`);
            }

            // Create reservation
            const reservation = {
                sku,
                units,
                orderId,
                userId,
                tier,
                createdAt: Timestamp.now(),
                expiresAt: Timestamp.fromMillis(Date.now() + expiresIn),
                status: 'active'
            };

            // Store in Firestore for persistence
            const reservationRef = await addDoc(
                collection(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS),
                reservation
            );

            // Also store in memory for quick access
            this.reservations.set(orderId, {
                ...reservation,
                id: reservationRef.id
            });

            console.log(`[UnifiedStock] ✅ Reserved ${units} units of ${sku} for order ${orderId}`, {
                available: total,
                reserved: reservedUnits,
                newReservation: units,
                remainingAvailable: available - units
            });

            // Log reservation movement
            await stockMovementService.logMovement({
                sku,
                lotCode: null,
                type: MOVEMENT_TYPE.RESERVATION,
                deltaUnits: -units,  // Negative because reducing available
                beforeTotal: available,
                afterTotal: available - units,
                sourceSystem: SOURCE_SYSTEM.ECOMMERCE,
                sourceId: orderId,
                userId,
                reason: 'Stock reserved for order',
                metadata: { reservationId: reservationRef.id, expiresIn }
            });

            return {
                success: true,
                reservationId: reservationRef.id,
                sku,
                units,
                orderId,
                expiresAt: reservation.expiresAt.toDate()
            };

        } catch (error) {
            console.error(`[UnifiedStock] Reservation failed for ${sku}:`, error);
            throw error;
        }
    }

    /**
     * Release stock reservation
     * @param {string} orderId - Order/cart ID
     * @param {object} metadata - Metadata
     * @returns {Promise<object>} Release result
     */
    async releaseReservation(orderId, metadata = {}) {
        if (!orderId) throw new Error('Order ID is required');

        const { userId = 'system', reason = 'Reservation released' } = metadata;

        try {
            // Find reservation in Firestore
            const q = query(
                collection(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS),
                where('orderId', '==', orderId),
                where('status', '==', 'active')
            );

            const snap = await getDocs(q);
            
            if (snap.empty) {
                console.warn(`[UnifiedStock] No active reservation found for order ${orderId}`);
                return { success: true, found: false };
            }

            const releasedItems = [];

            // Release all reservations for this order
            for (const reservationDoc of snap.docs) {
                const reservation = reservationDoc.data();
                
                // Mark as released
                await updateDoc(doc(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS, reservationDoc.id), {
                    status: 'released',
                    releasedAt: Timestamp.now(),
                    releasedBy: userId,
                    releaseReason: reason
                });

                // Remove from memory
                this.reservations.delete(orderId);

                // Log release movement
                await stockMovementService.logMovement({
                    sku: reservation.sku,
                    lotCode: null,
                    type: MOVEMENT_TYPE.RELEASE,
                    deltaUnits: reservation.units,  // Positive because returning to available
                    beforeTotal: 0,  // Available stock calculation
                    afterTotal: reservation.units,
                    sourceSystem: SOURCE_SYSTEM.ECOMMERCE,
                    sourceId: orderId,
                    userId,
                    reason,
                    metadata: { reservationId: reservationDoc.id, wasReservedUnits: reservation.units }
                });

                releasedItems.push({
                    sku: reservation.sku,
                    units: reservation.units
                });

                console.log(`[UnifiedStock] ✅ Released reservation: ${reservation.sku} x${reservation.units}`);
            }

            return {
                success: true,
                found: true,
                orderId,
                releasedItems
            };

        } catch (error) {
            console.error(`[UnifiedStock] Failed to release reservation:`, error);
            throw error;
        }
    }

    /**
     * Get total reserved units for a SKU
     * @param {string} sku - Product SKU
     * @returns {Promise<number>} Reserved units
     */
    async getReservedUnits(sku) {
        if (!sku) return 0;

        try {
            const now = Timestamp.now();
            const q = query(
                collection(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS),
                where('sku', '==', sku),
                where('status', '==', 'active'),
                where('expiresAt', '>', now)
            );

            const snap = await getDocs(q);
            const reservedTotal = snap.docs.reduce((sum, doc) => {
                return sum + (doc.data().units || 0);
            }, 0);

            return reservedTotal;

        } catch (error) {
            console.error(`[UnifiedStock] Error getting reserved units:`, error);
            return 0;  // Fail safe - don't block operations
        }
    }

    /**
     * Get all active reservations grouped by SKU
     * Used for bulk display in admin interfaces
     * @returns {Promise<Object>} { sku: totalReservedUnits }
     */
    async getAllReservations() {
        try {
            const now = Timestamp.now();
            const q = query(
                collection(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS),
                where('status', '==', 'active'),
                where('expiresAt', '>', now)
            );

            const snap = await getDocs(q);
            const reservationsBySku = {};

            snap.docs.forEach(doc => {
                const data = doc.data();
                const sku = data.sku;
                const units = data.units || 0;

                if (!reservationsBySku[sku]) {
                    reservationsBySku[sku] = 0;
                }
                reservationsBySku[sku] += units;
            });

            return reservationsBySku;

        } catch (error) {
            console.error(`[UnifiedStock] Error getting all reservations:`, error);
            return {};  // Fail safe
        }
    }

    /**
     * Clean up expired reservations
     * @returns {Promise<number>} Number of expired reservations cleaned
     */
    async cleanupExpiredReservations() {
        try {
            const now = Timestamp.now();
            const q = query(
                collection(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS),
                where('status', '==', 'active'),
                where('expiresAt', '<', now)
            );

            const snap = await getDocs(q);
            let cleaned = 0;

            for (const reservationDoc of snap.docs) {
                await updateDoc(doc(db, STOCK_COLLECTIONS.STOCK_RESERVATIONS, reservationDoc.id), {
                    status: 'expired',
                    expiredAt: now
                });
                cleaned++;
            }

            console.log(`[UnifiedStock] Cleaned ${cleaned} expired reservations`);
            return cleaned;

        } catch (error) {
            console.error(`[UnifiedStock] Error cleaning reservations:`, error);
            return 0;
        }
    }

    // ========================================================================
    // AGGREGATED STOCK (All Sources)
    // ========================================================================

    /**
     * Get stock for a SKU across ALL storage locations
     * @param {string} sku - Product SKU
     * @param {object} options - Fetch options
     * @returns {Promise<object>} Aggregated stock from all sources
     */
    async getAllStockForSku(sku, options = {}) {
        if (!sku) throw new Error('SKU is required');
        
        const { tier = 'default' } = options;

        try {
            // Fetch from all possible sources in parallel
            const [
                productStock,
                warehouseStock,
                lots,
                // Note: venue/market stock queried separately as they need IDs
            ] = await Promise.all([
                this.getProductStock(sku, tier, { useCache: true }).catch(() => null),
                this.getMainStock({ useCache: true, normalize: false }).catch(() => ({})),
                this.getLotStock(sku, { useCache: true, includeFrozen: false }).catch(() => [])
            ]);

            const warehouseValue = this.extractStockValue(warehouseStock[sku]);
            const lotsTotal = Array.isArray(lots) ? 
                lots.reduce((sum, lot) => sum + lot.units, 0) : 0;

            const result = {
                sku,
                ecommerce: {
                    stock: productStock?.Stock,
                    isUnlimited: productStock?.Stock === 'x',
                    numeric: this.extractStockValue(productStock?.Stock),
                    source: 'products_' + tier
                },
                warehouse: {
                    total: warehouseValue,
                    source: 'stockLevels',
                    hasLots: lotsTotal > 0,
                    lotTotal: lotsTotal,
                    lots: lots || []
                },
                // Use lot total if available, otherwise warehouse total
                effectiveTotal: lotsTotal > 0 ? lotsTotal : warehouseValue,
                // Detect discrepancies
                inSync: productStock?.Stock === 'x' || 
                        Math.abs(this.extractStockValue(productStock?.Stock) - warehouseValue) < 0.01,
                discrepancy: productStock?.Stock !== 'x' ? 
                    this.extractStockValue(productStock?.Stock) - warehouseValue : 0
            };

            console.log(`[UnifiedStock] Aggregated stock for ${sku}:`, {
                ecommerce: result.ecommerce.numeric,
                warehouse: result.warehouse.total,
                effective: result.effectiveTotal,
                inSync: result.inSync
            });

            return result;

        } catch (error) {
            console.error(`[UnifiedStock] Error getting all stock for ${sku}:`, error);
            throw error;
        }
    }

    // ========================================================================
    // BATCH OPERATIONS
    // ========================================================================

    /**
     * Update multiple SKUs at once
     * @param {object} updates - {sku: newTotal, ...}
     * @param {object} metadata - Metadata for all updates
     * @returns {Promise<void>}
     */
    async batchUpdateStock(updates, metadata = {}) {
        if (!updates || typeof updates !== 'object') {
            throw new Error('Updates object is required');
        }

        const skus = Object.keys(updates);
        if (skus.length === 0) return;

        try {
            const stockRef = doc(db, STOCK_COLLECTIONS.STOCK_LEVELS, STOCK_COLLECTIONS.STOCK_LEVELS_DOC);
            const snap = await getDoc(stockRef);
            const currentStock = snap.exists() ? (snap.data().stock || {}) : {};

            const newStock = { ...currentStock };

            skus.forEach(sku => {
                const newTotal = updates[sku];
                if (typeof newTotal !== 'number' || newTotal < 0) {
                    console.warn(`[UnifiedStock] Skipping invalid update for ${sku}: ${newTotal}`);
                    return;
                }

                const currentValue = currentStock[sku];
                
                if (typeof currentValue === 'number' || !currentValue) {
                    newStock[sku] = newTotal;
                } else {
                    newStock[sku] = {
                        ...currentValue,
                        total: newTotal,
                        lastUpdated: Timestamp.now(),
                        lastUpdatedBy: metadata.userId || metadata.userEmail || 'system'
                    };
                }
            });

            await setDoc(stockRef, { stock: newStock }, { merge: true });

            console.log(`[UnifiedStock] Batch updated ${skus.length} SKUs`);

            // Clear cache
            this.cache.delete('main_stock');

        } catch (error) {
            console.error('[UnifiedStock] Error in batch update:', error);
            throw error;
        }
    }

    // ========================================================================
    // UTILITY FUNCTIONS
    // ========================================================================

    /**
     * Check if SKU is low on stock
     * @param {string} sku - Product SKU
     * @param {number} threshold - Low stock threshold
     * @returns {Promise<boolean>}
     */
    async isLowStock(sku, threshold = 10) {
        const totals = await this.getStockTotals(sku);
        return totals.total > 0 && totals.total < threshold;
    }

    /**
     * Check if SKU is out of stock
     * @param {string} sku - Product SKU
     * @returns {Promise<boolean>}
     */
    async isOutOfStock(sku) {
        const totals = await this.getStockTotals(sku);
        return totals.total <= 0;
    }

    /**
     * Get low stock items
     * @param {number} threshold - Low stock threshold
     * @returns {Promise<Array>} Array of {sku, total}
     */
    async getLowStockItems(threshold = 10) {
        const stock = await this.getMainStock({ normalize: false });
        const lowStock = [];

        for (const [sku, value] of Object.entries(stock)) {
            const total = this.extractStockValue(value);
            if (total > 0 && total < threshold) {
                lowStock.push({ sku, total });
            }
        }

        return lowStock;
    }

    // ========================================================================
    // CACHE MANAGEMENT
    // ========================================================================

    /**
     * Clear cache for specific key or all cache
     * @param {string} key - Cache key (optional)
     */
    clearCache(key = null) {
        if (key) {
            this.cache.delete(key);
        } else {
            this.cache.clear();
        }
    }

    /**
     * Get cache statistics
     * @returns {object} Cache stats
     */
    getCacheStats() {
        return this.cache.getStats();
    }

    // ========================================================================
    // CLEANUP
    // ========================================================================

    /**
     * Unsubscribe from all subscriptions
     */
    cleanup() {
        this.subscriptions.forEach((unsubscribe, id) => {
            unsubscribe();
            console.log(`[UnifiedStock] Unsubscribed: ${id}`);
        });
        this.subscriptions.clear();
        this.cache.clear();
    }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const unifiedStockService = new UnifiedStockService();

export default unifiedStockService;

// Export the class for testing
export { UnifiedStockService };
